/**
 * ceoDataService.js — Carga de datos JSON estáticos para el CEO Executive Dashboard v2.
 *
 * Diferencia vs PCP/CM: No hay LocationSelector ni subcarpetas por location.
 * Todos los datos son consolidados (red completa).
 *
 * Estructura v2:
 *   DataDashCEO_v2/population/overview.json       -> Panel 1 (KPIs)
 *   DataDashCEO_v2/population/new_members.json    -> Panel 1 Dialog (lazy)
 *   DataDashCEO_v2/cost/summary.json              -> Panel 2 (KPIs)
 *   DataDashCEO_v2/cost/monthly.json              -> Panel 2 (monthly chart)
 *   DataDashCEO_v2/cost/by_location.json          -> Panel 2 (location chart)
 *   DataDashCEO_v2/cost/top_pcps.json             -> Panel 2 (table)
 *   DataDashCEO_v2/cost/top_facilities.json       -> Panel 2 (table)
 *   DataDashCEO_v2/quality/summary.json           -> Panel 3 (stats)
 *   DataDashCEO_v2/quality/top_measures.json      -> Panel 3 (top 10)
 *   DataDashCEO_v2/quality/measures.json           -> Panel 3 (all 60, lazy)
 *   DataDashCEO_v2/centers/performance.json       -> Panel 4
 *   DataDashCEO_v2/trends/trends.json             -> Panel 5
 *   DataDashCEO_v2/coding/summary.json            -> Panel 6 (KPIs)
 *   DataDashCEO_v2/coding/top_hccs.json           -> Panel 6 (table)
 *   DataDashCEO_v2/coding/recapture_by_pcp.json   -> Panel 6 (table)
 *   DataDashCEO_v2/engagement/engagement.json     -> Panel 7
 *   DataDashCEO_v2/performance/summary.json       -> Panel 8 (summary)
 *   DataDashCEO_v2/performance/employees.json     -> Panel 8 (detail, lazy)
 */

// ── POPULATION ─────────────────────────────────────────────────
export const getPopulationOverview = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/population/overview.json").then(m => m.default);

/** Lazy — solo cuando el usuario abre el Dialog de New Members */
export const getNewMembers = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/population/new_members.json").then(m => m.default);

// ── COST ───────────────────────────────────────────────────────
export const getCostSummary = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/cost/summary.json").then(m => m.default);

export const getCostMonthly = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/cost/monthly.json").then(m => m.default);

export const getCostByLocation = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/cost/by_location.json").then(m => m.default);

export const getCostTopPCPs = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/cost/top_pcps.json").then(m => m.default);

export const getCostTopFacilities = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/cost/top_facilities.json").then(m => m.default);

// ── QUALITY ────────────────────────────────────────────────────
export const getQualitySummary = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/quality/summary.json").then(m => m.default);

export const getTopMeasures = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/quality/top_measures.json").then(m => m.default);

/** Lazy — solo al expandir "Show all 60 measures" */
export const getAllMeasures = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/quality/measures.json").then(m => m.default);

// ── CENTERS ────────────────────────────────────────────────────
export const getCenterPerformance = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/centers/performance.json").then(m => m.default);

// ── TRENDS ─────────────────────────────────────────────────────
export const getTrends = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/trends/trends.json").then(m => m.default);

// ── CODING ─────────────────────────────────────────────────────
export const getCodingSummary = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/coding/summary.json").then(m => m.default);

export const getCodingTopHCCs = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/coding/top_hccs.json").then(m => m.default);

export const getCodingRecaptureByPCP = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/coding/recapture_by_pcp.json").then(m => m.default);

// ── ENGAGEMENT ─────────────────────────────────────────────────
export const getEngagement = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/engagement/engagement.json").then(m => m.default);

// ── PERFORMANCE ────────────────────────────────────────────────
export const getPerformanceSummary = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/performance/summary.json").then(m => m.default);

/** Lazy — solo cuando el usuario expande Team Performance detail */
export const getEmployees = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/performance/employees.json").then(m => m.default);

// ── CLINICAL PERFORMANCE ─────────────────────────────────────────
/** Lazy — datos de rendimiento clínico de PCPs (care gaps + coding) */
export const getClinicalPerformance = () =>
  import("../../../pages/Main/Temp/DataDashCEO_v2/performance/clinical_performance.json").then(m => m.default);
